import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as CopyIcon } from '../../../../../assets/icons/copy.svg';
import { ReactComponent as ExpandIcon } from '../../../../../assets/icons/expand-icon.svg';
import { ReactComponent as ChangeAvailabilityIcon } from '../../../../../assets/icons/ocpp/change-availability.svg';
import { ReactComponent as ChangeConfigurationCustomIcon } from '../../../../../assets/icons/ocpp/change-configuration-custom.svg';
import { ReactComponent as ChangeConfigurationIcon } from '../../../../../assets/icons/ocpp/change-configuration.svg';
import { ReactComponent as ClearCacheIcon } from '../../../../../assets/icons/ocpp/clear-cache.svg';
import { ReactComponent as ClearChargingProfileIcon } from '../../../../../assets/icons/ocpp/clear-charging-profile.svg';
import { ReactComponent as DefaultCommandIcon } from '../../../../../assets/icons/ocpp/default-command.svg';
import { ReactComponent as GetConfigurationIcon } from '../../../../../assets/icons/ocpp/get-configuration.svg';
import { ReactComponent as GetDiagnosticsIcon } from '../../../../../assets/icons/ocpp/get-diagnostics.svg';
import { ReactComponent as GetLocalListVersionIcon } from '../../../../../assets/icons/ocpp/get-local-list-version.svg';
import { ReactComponent as ResetCpIcon } from '../../../../../assets/icons/ocpp/reset-cp.svg';
import { ReactComponent as RmtStartTransactionIcon } from '../../../../../assets/icons/ocpp/rmt-start-transaction.svg';
import { ReactComponent as RmtStopTransactionIcon } from '../../../../../assets/icons/ocpp/rmt-stop-transaction.svg';
import { ReactComponent as SetChargingProfileIcon } from '../../../../../assets/icons/ocpp/set-charging-profile.svg';
import { ReactComponent as TriggerMessageIcon } from '../../../../../assets/icons/ocpp/trigger-message.svg';
import { ReactComponent as UnlockConnectorIcon } from '../../../../../assets/icons/ocpp/unlock-connector.svg';
import { ReactComponent as UpdateFirmwareIcon } from '../../../../../assets/icons/ocpp/update-firmware.svg';
import { ReactComponent as RefreshIcon } from '../../../../../assets/icons/refresh-circle.svg';
import { ocppMessageType } from '../../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../../../shared-components/button/Button';
import CustomDatePicker from '../../../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../../../shared-components/custom-input/CustomInput';
import DeleteWarning from '../../../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import ConvertDateTimeToUTC from '../../../../../shared-functions/ConvertDateTimeToUTC';
import ConvertUTCtoDateTime from '../../../../../shared-functions/ConvertUTCtoDateTime';
import device from '../../../../../utils/api/device';
import deviceHubApi from '../../../../../utils/api/device-hub';
import diagnosticFirmwareApi from '../../../../../utils/api/diagnostic-firmware';
import ocppEvents from '../../../../../utils/api/ocpp-events';

import SetChargingProfile from './ocpp-commands/SetChargingProfile';

const ID_TAG_EXPIRY_MS = 60 * 60 * 1000;

/**
 * Calculates the number of whole minutes remaining before a reserved idTag expires,
 * based on the server's 1-hour safety-net expiry from the moment it was fetched.
 * @param {number | null} fetchedAt - The timestamp (ms) when the idTag was fetched, or null if never fetched.
 * @returns {number} Minutes remaining until expiry (0 if already expired or never fetched).
 */
function getIdTagMinutesRemaining(fetchedAt: number | null): number {
  if (!fetchedAt) return 0;
  const remainingMs = fetchedAt + ID_TAG_EXPIRY_MS - Date.now();
  return remainingMs > 0 ? Math.ceil(remainingMs / 60000) : 0;
}

interface JsonCodeViewerProperties {
  data: any;
}

/**
 * Helper component to render syntax highlighted JSON with line numbers
 * @param {JsonCodeViewerProperties} properties - Component properties
 * @param {any} properties.data - The JSON object or string to format
 * @returns {React.ReactElement} The rendered JSON code viewer
 */
function JsonCodeViewer({ data }: JsonCodeViewerProperties): React.ReactElement {
  const jsonString = useMemo(() => {
    if (!data) return '{}';
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return data;
      }
    }
    return JSON.stringify(data, null, 2);
  }, [data]);

  const lines = useMemo(() => jsonString.split('\n'), [jsonString]);

  /**
   * Highlights a single line of JSON text
   * @param {string} line - The line string
   * @returns {React.ReactNode[]} The highlighted token nodes
   */
  const highlightJsonLine = (line: string): React.ReactNode[] => {
    const tokenRegex =
      /("(\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?|[{}[\],:])/g;

    const tokens: React.ReactNode[] = [];
    let lastIndex = 0;
    let match = tokenRegex.exec(line);

    while (match !== null) {
      if (match.index > lastIndex) {
        tokens.push(line.substring(lastIndex, match.index));
      }
      const token = match[0];
      if (token.startsWith('"') && token.endsWith(':')) {
        tokens.push(
          <span key={`k-${match.index}`} className="json-key">
            {token}
          </span>,
        );
      } else if (token.startsWith('"')) {
        tokens.push(
          <span key={`s-${match.index}`} className="json-string">
            {token}
          </span>,
        );
      } else if (token === 'true' || token === 'false') {
        tokens.push(
          <span key={`b-${match.index}`} className="json-boolean">
            {token}
          </span>,
        );
      } else if (token === 'null') {
        tokens.push(
          <span key={`n-${match.index}`} className="json-null">
            {token}
          </span>,
        );
      } else if (!Number.isNaN(Number(token)) && !token.startsWith('"')) {
        tokens.push(
          <span key={`num-${match.index}`} className="json-number">
            {token}
          </span>,
        );
      } else {
        tokens.push(
          <span key={`sym-${match.index}`} className="json-symbol">
            {token}
          </span>,
        );
      }
      lastIndex = tokenRegex.lastIndex;
      match = tokenRegex.exec(line);
    }
    if (lastIndex < line.length) {
      tokens.push(line.substring(lastIndex));
    }
    return tokens;
  };

  return (
    <div className="json-code-viewer">
      <div className="json-line-numbers">
        {lines.map((_, index) => (
          <div key={`num-${index + 1}`} className="line-number">
            {index + 1}
          </div>
        ))}
      </div>
      <div className="json-code-content">
        {lines.map((line, index) => (
          <div key={`line-${index + 1}`} className="code-line">
            {highlightJsonLine(line)}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Renders the command sidebar icon based on command type
 * @param {string} commandType - The OCPP message type identifier
 * @returns {React.ReactNode} The rendered SVG icon element
 */
const renderCommandSidebarIcon = (commandType: string): React.ReactNode => {
  switch (commandType) {
    case ocppMessageType.CHANGE_AVAILABILITY:
      return <ChangeAvailabilityIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.GET_CONFIGURATION:
      return <GetConfigurationIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.CHANGE_CONFIGURATION:
      return <ChangeConfigurationIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.CHANGE_CONFIGURATION_CUSTOM:
      return <ChangeConfigurationCustomIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.CLEAR_CACHE:
      return <ClearCacheIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.RMT_START_TRANSACTION:
      return <RmtStartTransactionIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.RMT_STOP_TRANSACTION:
      return <RmtStopTransactionIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.RESET_CP:
      return <ResetCpIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.UNLOCK_CONNECTOR:
      return <UnlockConnectorIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.TRIGGER_MESSAGE:
      return <TriggerMessageIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.UPDATE_FIRMWARE:
      return <UpdateFirmwareIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.GET_DIAGNOSTICS:
      return <GetDiagnosticsIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.GET_LOCAL_LIST_VERSION:
      return <GetLocalListVersionIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.SET_CHARGING_PROFILE:
      return <SetChargingProfileIcon className="ocpp-sidebar-icon" />;
    case ocppMessageType.CLEAR_CHARGING_PROFILE:
      return <ClearChargingProfileIcon className="ocpp-sidebar-icon" />;
    default:
      return <DefaultCommandIcon className="ocpp-sidebar-icon" />;
  }
};

/**
 * Renders an info circle SVG icon
 * @param {string} [className] - Optional CSS class name
 * @returns {React.ReactNode} The rendered SVG icon
 */
const renderInfoIcon = (className?: string): React.ReactNode => (
  <svg
    className={className || 'ocpp-info-icon'}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

/**
 * Zero-pads a number to at least 2 digits
 * @param {number} value - The number to pad
 * @returns {string} The zero-padded string
 */
const padToTwoDigits = (value: number): string => String(value).padStart(2, '0');

/**
 * Formats a Date in the local wall-clock "YYYY-MM-DD HH:mm:ss" string
 * format CustomDatePicker's value/minDate/maxDate use.
 * @param {Date} date - The date to format
 * @returns {string} The formatted date-time string
 */
const formatLocalDateTime = (date: Date): string =>
  `${date.getFullYear()}-${padToTwoDigits(date.getMonth() + 1)}-${padToTwoDigits(
    date.getDate(),
  )} ${padToTwoDigits(date.getHours())}:${padToTwoDigits(date.getMinutes())}:${padToTwoDigits(
    date.getSeconds(),
  )}`;

/**
 * Formats a Date as "YYYY-MM-DD" (local), matching CustomDatePicker's
 * minDate/maxDate format.
 * @param {Date} date - The date to format
 * @returns {string} The formatted date string
 */
const formatLocalDate = (date: Date): string => formatLocalDateTime(date).split(' ')[0];

/**
 * Returns "1 minute from now" in the local wall-clock "YYYY-MM-DD HH:mm:ss"
 * format CustomDatePicker's value/onChangeValue use — used to quick-fill
 * the firmware Download Date/Time field so it's immediately submittable.
 * @returns {string} The computed date-time string
 */
const getImmediateFirmwareRetrieveDateTime = (): string =>
  formatLocalDateTime(new Date(Date.now() + 60 * 1000));

/**
 * Returns today's date in "YYYY-MM-DD" format (local time), matching
 * CustomDatePicker's minDate/maxDate format.
 * @returns {string} Today's date string
 */
const getTodayDateString = (): string => formatLocalDate(new Date());

/**
 * Returns the date exactly 3 calendar months before today, in "YYYY-MM-DD"
 * format (local time) — the minimum selectable date for Get Diagnostics'
 * "Include Logs From" field.
 * @returns {string} The cutoff date string
 */
const getStartTimeMinDateString = (): string => {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - 3);
  return formatLocalDate(cutoff);
};

/**
 * Whether a CustomDatePicker "YYYY-MM-DD HH:mm:ss" value is later than the
 * current local moment.
 * @param {string | undefined} value - The date-time string to check
 * @returns {boolean} Whether the value is in the future
 */
const isFutureDateTime = (value: string | undefined): boolean =>
  Boolean(value) && (value as string) > formatLocalDateTime(new Date());

/**
 * Whether a CustomDatePicker "YYYY-MM-DD HH:mm:ss" value's date portion is
 * earlier than the given "YYYY-MM-DD" cutoff.
 * @param {string | undefined} value - The date-time string to check
 * @param {string} minDateString - The "YYYY-MM-DD" cutoff
 * @returns {boolean} Whether the value's date is before the cutoff
 */
const isBeforeMinDate = (value: string | undefined, minDateString: string): boolean =>
  Boolean(value) && (value as string).split(' ')[0] < minDateString;

interface OcppCommandInputOption {
  label: string;
  value: string;
  description?: string;
}

interface OcppCommandInput {
  label: string;
  value: string | number;
  key: string;
  valueRequired: boolean;
  type: 'number' | 'select-field' | string;
  options?: OcppCommandInputOption[];
  sendAsList?: boolean;
  minDate?: string;
}

interface OcppCommandProperties {
  name: string;
  type: string;
  isActive: boolean;
  disableCommand: boolean;
  description: string;
  whatHappens: string[];
  inputs: OcppCommandInput[];
}

/**
 * Pending (non-terminal) firmware job for a charging point, as returned by
 * GET .../api/admin/v1/firmware/jobs/pending
 */
interface PendingFirmwareJob {
  _id: string;
  domain: string;
  chargingPointSn: string;
  artifactId: string;
  status: string;
  transferScheme: string;
  scheduledRetrieveDate: string;
  retries: number;
  retryInterval: number;
  timeoutSeconds: number;
  sentAt: string;
  statusHistory: { value?: string; status: string; at: string }[];
}

/**
 * Pending (non-terminal) diagnostics request for a charging point, as
 * returned by GET .../api/admin/v1/diagnostics/requests/pending
 */
interface PendingDiagnosticsRequest {
  _id: string;
  chargingPointSn: string;
  status: string;
  transferScheme: string;
  startTime: string | null;
  stopTime: string | null;
  timeoutSeconds: number;
  retries: number;
  retryInterval: number;
  sentAt: string;
  statusHistory: { value?: string; status: string; at: string }[];
}

/**
 * Configuration options for GetConfiguration and ChangeConfiguration commands
 */
const getConfigurationOptions = (t: any) => [
  {
    label: t('Allow Offline Tx For Unknown Id'),
    value: 'AllowOfflineTxForUnknownId',
    description: t(
      'Allow Offline Tx For Unknown Id Desc',
      'Allows offline transactions for unknown IDs.',
    ),
  },
  {
    label: t('Authorization Cache Enabled'),
    value: 'AuthorizationCacheEnabled',
    description: t(
      'Authorization Cache Enabled Desc',
      'Enables the Authorization Cache when set to true.',
    ),
  },
  {
    label: t('Authorize Remote Tx Requests'),
    value: 'AuthorizeRemoteTxRequests',
    description: t(
      'Authorize Remote Tx Requests Desc',
      'Requires authorization before starting remote transactions.',
    ),
  },
  {
    label: t('Blink Repeat'),
    value: 'BlinkRepeat',
    description: t(
      'Blink Repeat Desc',
      'Number of times the Charge Point light blinks when signaling.',
    ),
  },
  {
    label: t('Clock Aligned Data Interval'),
    value: 'ClockAlignedDataInterval',
    description: t(
      'Clock Aligned Data Interval Desc',
      'Clock-aligned data interval in seconds; 0 disables clock-aligned data transmission.',
    ),
  },
  {
    label: t('Connection Time Out'),
    value: 'ConnectionTimeOut',
    description: t(
      'Connection Time Out Desc',
      'Time in seconds before a Preparing transaction is automatically canceled.',
    ),
  },
  {
    label: t('Get Configuration Max Keys'),
    value: 'GetConfigurationMaxKeys',
    description: t(
      'Get Configuration Max Keys Desc',
      'Maximum number of configuration keys per GetConfiguration request.',
    ),
  },
  {
    label: t('Heartbeat Interval'),
    value: 'HeartbeatInterval',
    description: t(
      'Heartbeat Interval Desc',
      'Inactivity interval before the Charge Point sends a Heartbeat request.',
    ),
  },
  {
    label: t('Local Authorize Offline'),
    value: 'LocalAuthorizeOffline',
    description: t(
      'Local Authorize Offline Desc',
      'Allows transactions for locally authorized IDs when offline.',
    ),
  },
  {
    label: t('Local Pre Authorize'),
    value: 'LocalPreAuthorize',
    description: t(
      'Local Pre Authorize Desc',
      'Allows locally authorized IDs to start transactions without Central System authorization.',
    ),
  },
  {
    label: t('Max Energy On Invalid Id'),
    value: 'MaxEnergyOnInvalidId',
    description: t(
      'Max Energy On Invalid Id Desc',
      'Maximum energy (Wh) delivered after an ID is invalidated.',
    ),
  },
  {
    label: t('Meter Values Aligned Data'),
    value: 'MeterValuesAlignedData',
    description: t(
      'Meter Values Aligned Data Desc',
      'Measurands included in clock-aligned MeterValues reports.',
    ),
  },
  {
    label: t('Meter Values Sampled Data'),
    value: 'MeterValuesSampledData',
    description: t(
      'Meter Values Sampled Data Desc',
      'Sampled measurands included in MeterValues reports.',
    ),
  },
  {
    label: t('Meter Value Sample Interval'),
    value: 'MeterValueSampleInterval',
    description: t(
      'Meter Value Sample Interval Desc',
      'Interval between metering data samples sent in MeterValues reports; 0 disables sampled data.',
    ),
  },
  {
    label: t('Minimum Status Duration'),
    value: 'MinimumStatusDuration',
    description: t(
      'Minimum Status Duration Desc',
      'Minimum status duration before sending a StatusNotification request.',
    ),
  },
  {
    label: t('Reset Retries'),
    value: 'ResetRetries',
    description: t(
      'Reset Retries Desc',
      'Number of retries for an unsuccessful Charge Point reset.',
    ),
  },
  {
    label: t('Connector Phase Rotation'),
    value: 'ConnectorPhaseRotation',
    description: t(
      'Connector Phase Rotation Desc',
      'Phase rotation per connector, reported as a CSL (e.g., 0.RST, 1.RST).',
    ),
  },
  {
    label: t('Stop Transaction On EV Side Disconnect'),
    value: 'StopTransactionOnEVSideDisconnect',
    description: t(
      'Stop Transaction On EV Side Disconnect Desc',
      'Stops the transaction when the EV cable is disconnected.',
    ),
  },
  {
    label: t('Stop Transaction On Invalid Id'),
    value: 'StopTransactionOnInvalidId',
    description: t(
      'Stop Transaction On Invalid Id Desc',
      'Stops the transaction when the StartTransaction ID is not authorized.',
    ),
  },
  {
    label: t('Stop Txn Aligned Data'),
    value: 'StopTxnAlignedData',
    description: t(
      'Stop Txn Aligned Data Desc',
      'Clock-aligned measurands included in StopTransaction meter data.',
    ),
  },
  {
    label: t('Stop Txn Sampled Data'),
    value: 'StopTxnSampledData',
    description: t(
      'Stop Txn Sampled Data Desc',
      'Sampled measurands included in StopTransaction data.',
    ),
  },
  {
    label: t('Transaction Message Attempts'),
    value: 'TransactionMessageAttempts',
    description: t(
      'Transaction Message Attempts Desc',
      'Number of retries for failed transaction-related messages.',
    ),
  },
  {
    label: t('Transaction Message Retry Interval'),
    value: 'TransactionMessageRetryInterval',
    description: t(
      'Transaction Message Retry Interval Desc',
      'Wait time before retrying a failed transaction-related message.',
    ),
  },
  {
    label: t('Unlock Connector On EV Side Disconnect'),
    value: 'UnlockConnectorOnEVSideDisconnect',
    description: t(
      'Unlock Connector On EV Side Disconnect Desc',
      'Unlocks the connector when the EV cable is disconnected.',
    ),
  },
  {
    label: t('WebSocket Ping Interval'),
    value: 'WebSocketPingInterval',
    description: t(
      'WebSocket Ping Interval Desc',
      'WebSocket ping interval in seconds; 0 disables client-side Ping/Pong.',
    ),
  },
  {
    label: t('Number Of Connectors'),
    value: 'NumberOfConnectors',
    description: t('Number Of Connectors Desc', 'Number of physical charging connectors.'),
  },
  {
    label: t('Meter Values Aligned Data Max Length'),
    value: 'MeterValuesAlignedDataMaxLength',
    description: t(
      'Meter Values Aligned Data Max Length Desc',
      'Maximum number of items in MeterValuesAlignedData.',
    ),
  },
  {
    label: t('Meter Values Sampled Data Max Length'),
    value: 'MeterValuesSampledDataMaxLength',
    description: t(
      'Meter Values Sampled Data Max Length Desc',
      'Maximum number of items in MeterValuesSampledData.',
    ),
  },
  {
    label: t('Connector Phase Rotation Max Length'),
    value: 'ConnectorPhaseRotationMaxLength',
    description: t(
      'Connector Phase Rotation Max Length Desc',
      'Maximum number of items in ConnectorPhaseRotation.',
    ),
  },
  {
    label: t('Stop Txn Aligned Data Max Length'),
    value: 'StopTxnAlignedDataMaxLength',
    description: t(
      'Stop Txn Aligned Data Max Length Desc',
      'Maximum number of items in StopTxnAlignedData.',
    ),
  },
  {
    label: t('Stop Txn Sampled Data Max Length'),
    value: 'StopTxnSampledDataMaxLength',
    description: t(
      'Stop Txn Sampled Data Max Length Desc',
      'Maximum number of items in StopTxnSampledData.',
    ),
  },
  { label: t('Supported Feature Profiles'), value: 'SupportedFeatureProfiles', description: '' },
  {
    label: t('Supported Feature Profiles Max Length'),
    value: 'SupportedFeatureProfilesMaxLength',
    description: t(
      'Supported Feature Profiles Max Length Desc',
      'List of supported OCPP Feature Profiles.',
    ),
  },
  {
    label: t('Supported File Transfer Protocols'),
    value: 'SupportedFileTransferProtocols',
    description: t(
      'Supported File Transfer Protocols Desc',
      'List of supported file transfer protocols.',
    ),
  },
  {
    label: t('Cpo Name'),
    value: 'CpoName',
    description: t('Cpo Name Desc', 'Charge Point Operator name.'),
  },
  {
    label: t('Security Profile'),
    value: 'SecurityProfile',
    description: t('Security Profile Desc', 'Security profile used by the Charge Point.'),
  },
  {
    label: t('Certificate Store Max Length'),
    value: 'CertificateStoreMaxLength',
    description: t('Certificate Store Max Length Desc', 'Maximum number of certificates stored.'),
  },
  {
    label: t('Certificate Signed Max Chain Size'),
    value: 'CertificateSignedMaxChainSize',
    description: t('Certificate Signed Max Chain Size Desc', 'Maximum certificate chain size.'),
  },
  {
    label: t('Additional Root Certificate Check'),
    value: 'AdditionalRootCertificateCheck',
    description: t(
      'Additional Root Certificate Check Desc',
      'Checks if the new root certificate is signed by the existing root certificate.',
    ),
  },
  {
    label: t('Reserve Connector Zero Supported'),
    value: 'ReserveConnectorZeroSupported',
    description: t(
      'Reserve Connector Zero Supported Desc',
      'Supports reservations on connector 0 when set to true.',
    ),
  },
  {
    label: t('Local Auth List Enabled'),
    value: 'LocalAuthListEnabled',
    description: t(
      'Local Auth List Enabled Desc',
      'Enables the Local Authorization List when set to true.',
    ),
  },
  {
    label: t('Local Auth List Max Length'),
    value: 'LocalAuthListMaxLength',
    description: t(
      'Local Auth List Max Length Desc',
      'Maximum number of IDs stored in the Local Authorization List.',
    ),
  },
  {
    label: t('Send Local List Max Length'),
    value: 'SendLocalListMaxLength',
    description: t(
      'Send Local List Max Length Desc',
      'Maximum number of identifications that can be send in a single SendLocalList.req',
    ),
  },
  {
    label: t('Charge Profile Max Stack Level'),
    value: 'ChargeProfileMaxStackLevel',
    description: t(
      'Charge Profile Max Stack Level Desc',
      'Maximum stack level and charging schedules per profile purpose.',
    ),
  },
  {
    label: t('Charging Schedule Allowed Charging Rate Unit'),
    value: 'ChargingScheduleAllowedChargingRateUnit',
    description: t(
      'Charging Schedule Allowed Charging Rate Unit Desc',
      'Supported charging rate units: Current and Power.',
    ),
  },
  {
    label: t('Charging Schedule Max Periods'),
    value: 'ChargingScheduleMaxPeriods',
    description: t(
      'Charging Schedule Max Periods Desc',
      'Maximum number of periods allowed per ChargingSchedule.',
    ),
  },
  {
    label: t('Connector Switch 3 to 1 Phase Supported'),
    value: 'ConnectorSwitch3to1PhaseSupported',
    description: t(
      'Connector Switch 3 to 1 Phase Supported Desc',
      'Supports switching from 3-phase to 1-phase during a transaction.',
    ),
  },
  {
    label: t('Max Charging Profiles Installed'),
    value: 'MaxChargingProfilesInstalled',
    description: t(
      'Max Charging Profiles Installed Desc',
      'Maximum number of Charging Profiles that can be installed at once.',
    ),
  },
  {
    label: t('Vendor Id'),
    value: 'vendorId',
    description: t('Vendor Id Desc', 'Charge Point vendor identifier.'),
  },
  {
    label: t('Charge Point SN'),
    value: 'chargePointSN',
    description: t('Charge Point SN Desc', 'Charge Point serial number.'),
  },
  {
    label: t('Custom Idle Fee After Stop'),
    value: 'CustomIdleFeeAfterStop',
    description: t('Custom Idle Fee After Stop Desc', 'Idle fee applied after charging stops.'),
  },
  {
    label: t('Charging Parameters Enabled'),
    value: 'ChargingParametersEnabled',
    description: t('Charging Parameters Enabled Desc', 'Enables charging parameter configuration.'),
  },
  {
    label: t('Security Event Enable'),
    value: 'SecurityEvtEnable',
    description: t('Security Event Enable Desc', 'Enables security event notifications.'),
  },
  {
    label: t('Websocket Fragment Enabled'),
    value: 'WebsocketFragmentEnabled',
    description: t('Websocket Fragment Enabled Desc', 'Enables WebSocket message fragmentation.'),
  },
  {
    label: t('Disable Light While Idle'),
    value: 'DisableLightWhileIdle',
    description: t('Disable Light While Idle Desc', 'Disables Charge Point lighting while idle.'),
  },
  {
    label: t('Net Auto Switch Enabled'),
    value: 'NetAutoSwitchEnabled',
    description: t('Net Auto Switch Enabled Desc', 'Enables automatic network switching.'),
  },
  {
    label: t('Free Mode Active'),
    value: 'FreeModeActive',
    description: t('Free Mode Active Desc', 'Enables free charging mode.'),
  },
  {
    label: t('Free Mode Rfid'),
    value: 'FreeModeRfid',
    description: t('Free Mode Rfid Desc', 'RFID used for free charging mode.'),
  },
];

/**
 * Trigger options for TriggerMessage command
 */
const getTriggerOptions = (t: any) => [
  {
    label: t('Boot Notification'),
    value: 'BootNotification',
    description: t('Boot Notification Desc', 'Triggers a BootNotification request.'),
  },
  {
    label: t('Diagnostics Status Notification'),
    value: 'DiagnosticsStatusNotification',
    description: t(
      'Diagnostics Status Notification Desc',
      'Triggers a DiagnosticsStatusNotification request.',
    ),
  },
  {
    label: t('Firmware Status Notification'),
    value: 'FirmwareStatusNotification',
    description: t(
      'Firmware Status Notification Desc',
      'Triggers a FirmwareStatusNotification request.',
    ),
  },
  {
    label: t('Heartbeat'),
    value: 'Heartbeat',
    description: t('Heartbeat Desc', 'Triggers a Heartbeat request.'),
  },
  {
    label: t('Meter Values'),
    value: 'MeterValues',
    description: t('Meter Values Desc', 'Triggers a MeterValues request.'),
  },
  {
    label: t('Status Notification'),
    value: 'StatusNotification',
    description: t('Status Notification Desc', 'Triggers a StatusNotification request.'),
  },
];

interface OperationsProperties {
  onActionTriggered?: () => void;
}

/**
 * Device summary details interface for displaying charger information
 */
interface DeviceSummaryDetails {
  brand: string;
  model: string;
  chargingPointSn: string;
  firmware: string;
  tariff: string;
}

/**
 * OCPP operations and command management console
 * @param {OperationsProperties} [properties] - Component properties
 * @param {() => void} [properties.onActionTriggered] - Callback when an action is submitted
 * @returns {React.ReactElement} The rendered Operations component
 */
export default function Operations({
  onActionTriggered,
}: OperationsProperties = {}): React.ReactElement {
  const { t } = useTranslation('devices');
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const isDeviceHub = window.location.pathname.includes('ev-device-hub');
  const { selectedChargingPoint } = useAppSelector((s) => s.device);
  const [searchParameters] = useSearchParams();
  const hideSidebar = searchParameters.get('hideSidebar') === 'true';
  const [deviceSummary, setDeviceSummary] = useState<DeviceSummaryDetails>({
    brand: '',
    model: selectedChargingPoint?.modelSeries || '',
    chargingPointSn: selectedChargingPoint?.chargingPointSn || selectedChargingPoint?.name || '',
    firmware: '',
    tariff: '',
  });
  const [formStatus, setFormStatus] = useState({
    buttonLoading: false,
  });

  const [modalView, setModalView] = useState<{
    status: boolean;
    title: string;
    content: React.ReactElement;
  }>({
    status: false,
    title: '',
    content: <div />,
  });

  const [ocppConsoleLogs, setOcppConsoleLogs] = useState<{
    request: any;
    response: any;
    isLoadingResponse: boolean;
  }>({
    request: null,
    response: null,
    isLoadingResponse: false,
  });

  const [expandedModal, setExpandedModal] = useState<{
    isOpen: boolean;
    title: string;
    data: any;
  }>({
    isOpen: false,
    title: '',
    data: null,
  });

  const pollingIntervalReference = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutReference = useRef<NodeJS.Timeout | null>(null);

  const [activeCommand, setActiveCommands] = useState<OcppCommandProperties>();
  const [connectorOptions, setConnectorOptions] = useState<OcppCommandInputOption[]>([]);
  const [firmwareOptions, setFirmwareOptions] = useState<OcppCommandInputOption[]>([]);
  const [protocolOptions, setProtocolOptions] = useState<OcppCommandInputOption[]>([]);
  const [idTagInfo, setIdTagInfo] = useState<{
    value: string;
    loading: boolean;
    fetchedAt: number | null;
  }>({
    value: '',
    loading: false,
    fetchedAt: null,
  });
  const [, forceIdTagTick] = useState(0);

  const [pendingFirmwareJob, setPendingFirmwareJob] = useState<{
    loading: boolean;
    hasPendingJob: boolean;
    firmwareJob: PendingFirmwareJob | null;
  }>({
    loading: false,
    hasPendingJob: false,
    firmwareJob: null,
  });
  const [showCancelFirmwareModal, setShowCancelFirmwareModal] = useState(false);
  const [cancellingFirmwareJob, setCancellingFirmwareJob] = useState(false);

  const [pendingDiagnosticsRequest, setPendingDiagnosticsRequest] = useState<{
    loading: boolean;
    hasPendingRequest: boolean;
    diagnosticsRequest: PendingDiagnosticsRequest | null;
  }>({
    loading: false,
    hasPendingRequest: false,
    diagnosticsRequest: null,
  });
  const [showCancelDiagnosticsModal, setShowCancelDiagnosticsModal] = useState(false);
  const [cancellingDiagnosticsRequest, setCancellingDiagnosticsRequest] = useState(false);

  const [startTimeError, setStartTimeError] = useState('');
  const [stopTimeError, setStopTimeError] = useState('');
  const [firmwareScheduleMode, setFirmwareScheduleMode] = useState<'schedule' | 'immediate'>(
    'schedule',
  );

  const [availableCommands, setAvailableCommands] = useState<OcppCommandProperties[]>([
    {
      name: t('changeAvailability'),
      type: ocppMessageType.CHANGE_AVAILABILITY,
      isActive: true,
      disableCommand: false,
      description: t(
        'changeAvailabilityDesc',
        'Changes the operational availability of the charging station or a specific connector.',
      ),
      whatHappens: [
        t(
          'changeAvailabilityWhat1',
          'Sets the operational status of the selected connector or whole charging station to Operative or Inoperative.',
        ),
        t(
          'changeAvailabilityWhat2',
          'Inoperative connectors will not accept new charging sessions while ongoing sessions complete.',
        ),
      ],
      inputs: [
        {
          label: t('connectorID'),
          value: '',
          key: 'connectorId',
          valueRequired: true,
          type: 'select-field',
          options: [],
        },
        {
          label: t('availabilityType'),
          value: '',
          key: 'type',
          valueRequired: true,
          type: 'select-field',
          options: [
            {
              label: t('operative'),
              value: 'Operative',
              description: t('operativeDesc', 'Charge point is available for charging'),
            },
            {
              label: t('inoperative'),
              value: 'Inoperative',
              description: t('inoperativeDesc', 'Charge point is not available for charging.'),
            },
          ],
        },
      ],
    },
    {
      name: t('getConfiguration'),
      type: ocppMessageType.GET_CONFIGURATION,
      isActive: false,
      disableCommand: false,
      description: t(
        'getConfigurationDesc',
        'Retrieves configuration parameter values from the charging station.',
      ),
      whatHappens: [
        t(
          'getConfigurationWhat1',
          'Requests current configuration key values stored in the charging station.',
        ),
        t(
          'getConfigurationWhat2',
          'If no specific key is selected, the station returns all supported configuration parameters.',
        ),
      ],
      inputs: [
        {
          label: t('configurationKey'),
          value: '',
          key: 'keys',
          valueRequired: false,
          type: 'select-field',
          options: getConfigurationOptions(t),
          sendAsList: true,
        },
      ],
    },
    {
      name: t('changeConfiguration'),
      type: ocppMessageType.CHANGE_CONFIGURATION,
      isActive: false,
      disableCommand: false,
      description: t(
        'changeConfigurationDesc',
        'Updates the value of a standard OCPP configuration key on the charging station.',
      ),
      whatHappens: [
        t(
          'changeConfigurationWhat1',
          'Transmits the updated configuration parameter value to the charging station.',
        ),
        t(
          'changeConfigurationWhat2',
          'The station responds with Accepted, Rejected, RebootRequired, or NotSupported.',
        ),
      ],
      inputs: [
        {
          label: t('configurationKey'),
          value: '',
          key: 'key',
          valueRequired: true,
          type: 'select-field',
          options: getConfigurationOptions(t),
        },
        {
          label: t('value'),
          value: '',
          key: 'value',
          valueRequired: true,
          type: 'text',
        },
      ],
    },
    {
      name: t('changeConfiguration(Custom)'),
      type: ocppMessageType.CHANGE_CONFIGURATION_CUSTOM,
      isActive: false,
      disableCommand: false,
      description: t(
        'changeConfigurationCustomDesc',
        'Sends a custom or vendor-specific configuration parameter with custom data type formatting.',
      ),
      whatHappens: [
        t(
          'changeConfigurationCustomWhat1',
          'Allows setting proprietary vendor configuration keys not in standard OCPP specifications.',
        ),
        t(
          'changeConfigurationCustomWhat2',
          'Applies the string or boolean payload directly to the station configuration.',
        ),
      ],
      inputs: [
        {
          label: t('configurationKey'),
          value: '',
          key: 'key',
          valueRequired: true,
          type: 'text',
        },
        {
          label: t('inputType'),
          value: '',
          key: 'inputType',
          valueRequired: true,
          type: 'select-field',
          options: [
            {
              label: t('string'),
              value: 'string',
            },
            {
              label: t('boolean'),
              value: 'boolean',
            },
          ],
        },
        {
          label: t('value'),
          value: '',
          key: 'value',
          valueRequired: true,
          type: 'text',
        },
      ],
    },
    {
      name: t('clearCache'),
      type: ocppMessageType.CLEAR_CACHE,
      isActive: false,
      disableCommand: false,
      description: t(
        'clearCacheDesc',
        'Clears the stored data and resets the cache on the charging station.',
      ),
      whatHappens: [
        t('clearCacheWhat1', 'All cached data on the charging station will be cleared.'),
        t('clearCacheWhat2', 'This does not affect any active transactions.'),
      ],
      inputs: [],
    },
    {
      name: t('remoteStartTransaction'),
      type: ocppMessageType.RMT_START_TRANSACTION,
      isActive: false,
      disableCommand: false,
      description: t(
        'remoteStartTransactionDesc',
        'Remotely initiates a charging transaction on the specified connector.',
      ),
      whatHappens: [
        t(
          'remoteStartTransactionWhat1',
          'Instructs the charging station to authorize and start delivering power on the selected connector.',
        ),
        t(
          'remoteStartTransactionWhat2',
          'The station will transition to charging and emit transaction updates.',
        ),
      ],
      inputs: [
        {
          label: t('connectorID'),
          value: '',
          key: 'connectorId',
          valueRequired: true,
          type: 'select-field',
          options: [],
        },
      ],
    },
    {
      name: t('remoteStopTransaction'),
      type: ocppMessageType.RMT_STOP_TRANSACTION,
      isActive: false,
      disableCommand: false,
      description: t(
        'remoteStopTransactionDesc',
        'Remotely stops an ongoing charging transaction using its transaction ID.',
      ),
      whatHappens: [
        t(
          'remoteStopTransactionWhat1',
          'Halts energy delivery for the ongoing session identified by the transaction ID.',
        ),
        t(
          'remoteStopTransactionWhat2',
          'The charging station concludes the session and sends the final meter value report.',
        ),
      ],
      inputs: [
        {
          label: t('transactionID'),
          value: '',
          key: 'transactionId',
          valueRequired: true,
          type: 'number',
        },
      ],
    },
    {
      name: t('reset'),
      type: ocppMessageType.RESET_CP,
      isActive: false,
      disableCommand: false,
      description: t('resetDesc', 'Performs a soft or hard system reboot of the charging station.'),
      whatHappens: [
        t(
          'resetWhat1',
          'Soft reset restarts software processes while attempting to preserve ongoing transactions gracefully.',
        ),
        t(
          'resetWhat2',
          'Hard reset immediately restarts the physical hardware and terminates ongoing operations.',
        ),
      ],
      inputs: [
        {
          label: t('resetType'),
          value: '',
          key: 'resetType',
          valueRequired: true,
          type: 'select-field',
          options: [
            {
              label: t('hard'),
              value: 'Hard',
              description: t(
                'hardResetDesc',
                'Restarts the Charge Point hardware without gracefully stopping ongoing transactions.',
              ),
            },
            {
              label: t('soft'),
              value: 'Soft',
              description: t(
                'softResetDesc',
                'Gracefully stops ongoing transactions and restarts the Charge Point software.',
              ),
            },
          ],
        },
      ],
    },
    {
      name: t('unlockConnector'),
      type: ocppMessageType.UNLOCK_CONNECTOR,
      isActive: false,
      disableCommand: false,
      description: t(
        'unlockConnectorDesc',
        'Unlocks the physical cable locking mechanism on the specified connector.',
      ),
      whatHappens: [
        t(
          'unlockConnectorWhat1',
          'Forces the electromechanical locking pin on the specified connector to release.',
        ),
        t(
          'unlockConnectorWhat2',
          'Allows the driver or operator to safely unplug the charging cable.',
        ),
      ],
      inputs: [
        {
          label: t('connectorID'),
          value: '',
          key: 'connectorId',
          valueRequired: true,
          type: 'select-field',
          options: [],
        },
      ],
    },
    {
      name: t('triggerMessage'),
      type: ocppMessageType.TRIGGER_MESSAGE,
      isActive: false,
      disableCommand: false,
      description: t(
        'triggerMessageDesc',
        'Triggers the charging station to immediately send a specific OCPP message.',
      ),
      whatHappens: [
        t(
          'triggerMessageWhat1',
          'Requests the charge point to emit a specific diagnostic message (e.g. BootNotification, MeterValues, StatusNotification).',
        ),
        t(
          'triggerMessageWhat2',
          'Enables instant telemetry inspection without waiting for scheduled intervals.',
        ),
      ],
      inputs: [
        {
          label: t('triggerMessage'),
          value: '',
          key: 'requestedMessage',
          valueRequired: true,
          type: 'select-field',
          options: getTriggerOptions(t),
        },
        {
          label: t('connectorID'),
          value: '',
          key: 'connectorId',
          valueRequired: false,
          type: 'select-field',
          options: [],
        },
      ],
    },
    {
      name: t('updateFirmware'),
      type: ocppMessageType.UPDATE_FIRMWARE,
      isActive: false,
      disableCommand: false,
      description: t(
        'updateFirmwareDesc',
        'Instructs the charging station to download and install a firmware update using the selected transfer protocol and version.',
      ),
      whatHappens: [
        t(
          'updateFirmwareWhat1',
          'Directs the station to retrieve the binary firmware payload using the selected protocol and version.',
        ),
        t(
          'updateFirmwareWhat2',
          'Progress updates will be reported via FirmwareStatusNotification messages.',
        ),
        t(
          'updateFirmwareWhat3',
          'Download Date/Time schedules when the station should start retrieving the firmware — it does not start the download immediately.',
        ),
      ],
      inputs: [
        {
          label: t('protocol', 'Protocol'),
          value: '',
          key: 'protocol',
          valueRequired: true,
          type: 'select-field',
          options: [],
        },
        {
          label: t('versions'),
          value: '',
          key: 'artifactId',
          valueRequired: true,
          type: 'select-field',
          options: [],
        },
        {
          label: t('retries'),
          value: '',
          key: 'retries',
          valueRequired: false,
          type: 'number',
        },
        {
          label: t('retryInterval'),
          value: '',
          key: 'retryInterval',
          valueRequired: false,
          type: 'number',
        },
        {
          label: t('retrieveDateTimeUTC', 'Download Date/Time'),
          value: '',
          key: 'scheduledRetrieveDate',
          valueRequired: true,
          type: 'date-picker',
        },
      ],
    },
    {
      name: t('getDiagnostics'),
      type: ocppMessageType.GET_DIAGNOSTICS,
      isActive: false,
      disableCommand: false,
      description: t(
        'getDiagnosticsDesc',
        'Requests the charging station to collect its diagnostic logs and upload them using the selected transfer protocol.',
      ),
      whatHappens: [
        t(
          'getDiagnosticsWhat1',
          'The charging station packages system logs, error dumps, and hardware diagnostics into an upload archive.',
        ),
        t(
          'getDiagnosticsWhat2',
          'DiagnosticsStatusNotification messages will be sent to report upload progress.',
        ),
        t(
          'getDiagnosticsWhat3',
          "Include Logs From and Include Logs Until limit the diagnostics to logs recorded within that window — leave both blank to include the station's full log history.",
        ),
        t(
          'getDiagnosticsWhat4',
          'Include Logs From can be at most 3 months in the past, and cannot be a future date/time.',
        ),
        t(
          'getDiagnosticsWhat5',
          'Include Logs Until must be later than Include Logs From, and cannot be a future date/time.',
        ),
      ],
      inputs: [
        // {
        //   label: t('locationDirectoryURI'),
        //   value: '',
        //   key: 'location',
        //   valueRequired: true,
        //   type: 'text',
        // },
        {
          label: t('protocol', 'Protocol'),
          value: '',
          key: 'protocol',
          valueRequired: true,
          type: 'select-field',
          options: [],
        },
        {
          label: t('retries'),
          value: '',
          key: 'retries',
          valueRequired: false,
          type: 'number',
        },
        {
          label: t('retryInterval'),
          value: '',
          key: 'retryInterval',
          valueRequired: false,
          type: 'number',
        },
        {
          label: t('startDateTimeUTC', 'Include Logs From'),
          value: '',
          key: 'startTime',
          valueRequired: false,
          type: 'date-picker',
        },
        {
          label: t('stopDateTimeUTC', 'Include Logs Until'),
          value: '',
          key: 'stopTime',
          valueRequired: false,
          type: 'date-picker',
        },
      ],
    },
    {
      name: t('getLocalListVersion'),
      type: ocppMessageType.GET_LOCAL_LIST_VERSION,
      isActive: false,
      disableCommand: false,
      description: t(
        'getLocalListVersionDesc',
        'Retrieves the current version number of the Local Authorization List stored on the charging station.',
      ),
      whatHappens: [
        t(
          'getLocalListVersionWhat1',
          'Queries the local whitelist database version stored in station memory.',
        ),
        t(
          'getLocalListVersionWhat2',
          'Returns version 0 if the local authorization list is empty or unsupported.',
        ),
      ],
      inputs: [],
    },
    {
      name: t('setChargingProfile'),
      type: ocppMessageType.SET_CHARGING_PROFILE,
      isActive: false,
      disableCommand: false,
      description: t(
        'setChargingProfileDesc',
        'Configures smart charging schedule profiles, power limits, and stack levels on the charging station.',
      ),
      whatHappens: [
        t(
          'setChargingProfileWhat1',
          'Applies power (Watts) or current (Amperes) limits across specified schedule periods.',
        ),
        t(
          'setChargingProfileWhat2',
          'Controls smart charging dispatch, tariff optimization, and local load management.',
        ),
      ],
      // Inputs handled in SetChargingProfile component
      inputs: [],
    },
    {
      name: t('clearChargingProfile'),
      type: ocppMessageType.CLEAR_CHARGING_PROFILE,
      isActive: false,
      disableCommand: false,
      description: t(
        'clearChargingProfileDesc',
        'Clears configured smart charging profiles from the charging station.',
      ),
      whatHappens: [
        t(
          'clearChargingProfileWhat1',
          'Removes installed profiles matching specified ID, connector, or purpose criteria.',
        ),
        t(
          'clearChargingProfileWhat2',
          'Restores default unconstrained charging capacity to the charge point.',
        ),
      ],
      inputs: [
        {
          label: 'ID',
          value: '',
          key: 'id',
          valueRequired: false,
          type: 'number',
        },
        {
          label: t('connectorID'),
          value: '',
          key: 'connectorId',
          valueRequired: false,
          type: 'select-field',
          options: [],
        },
        {
          label: t('chargingProfilePurpose'),
          value: '',
          key: 'chargingProfilePurpose',
          valueRequired: false,
          type: 'select-field',
          options: [
            {
              label: t('chargePointMaxProfile'),
              value: ' ChargePointMaxProfile',
              description: t(
                'chargePointMaxProfileDesc',
                'Maximum power or current available for the Charge Point.',
              ),
            },
            {
              label: t('txDefaultProfile'),
              value: ' TxDefaultProfile',
              description: t(
                'txDefaultProfileDesc',
                'Default charging profile used for new transactions.',
              ),
            },
            {
              label: t('txProfile'),
              value: ' TxProfile',
              description: t(
                'txProfileDesc',
                'Charging profile applied to the current transaction until it ends.',
              ),
            },
          ],
        },
        {
          label: t('stackLevel'),
          value: '',
          key: 'stackLevel',
          valueRequired: false,
          type: 'number',
        },
      ],
    },
  ]);

  /**
   * Switches the active sidebar menu command.
   * Cancels any in-flight polling for the previous command so that stale
   * log data cannot overwrite the reset state after the switch.
   * @param {string} key - The OCPP command type key to activate
   */
  const handleChangeActiveMenu = (key: string): void => {
    if (pollingIntervalReference.current) {
      clearInterval(pollingIntervalReference.current);
      pollingIntervalReference.current = null;
    }
    if (pollingTimeoutReference.current) {
      clearTimeout(pollingTimeoutReference.current);
      pollingTimeoutReference.current = null;
    }

    setAvailableCommands((previousCommands) =>
      previousCommands.map((command) => ({
        ...command,
        isActive: command.type === key,
      })),
    );
    setOcppConsoleLogs({
      request: null,
      response: null,
      isLoadingResponse: false,
    });
    setFirmwareScheduleMode('schedule');
  };

  /**
   * Updates an input value for a given command type
   * @param {string} key - The OCPP command type key
   * @param {string} name - The input field name
   * @param {string | number} value - The updated input value
   */
  const handleChangeValue = (key: string, name: string, value: string | number): void => {
    setAvailableCommands((previousCommands) =>
      previousCommands.map((command) => {
        if (command.type === key) {
          const updatedInputs = command.inputs.map((input) => {
            if (input.key === name) {
              return {
                ...input,
                value,
              };
            }
            return input;
          });

          return {
            ...command,
            inputs: updatedInputs,
          };
        }
        return command;
      }),
    );
  };

  useEffect(() => {
    return () => {
      if (pollingIntervalReference.current) {
        clearInterval(pollingIntervalReference.current);
      }
      if (pollingTimeoutReference.current) {
        clearTimeout(pollingTimeoutReference.current);
      }
    };
  }, []);

  /**
   * Copy JSON text to clipboard and trigger notification
   * @param {string} text - Text to copy
   * @param {string} type - Log type label
   */
  const handleCopy = (text: string, type: string): void => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    dispatch(
      setNotificationMessage({
        status: true,
        message: `${type} ${t('copiedToClipboard', 'copied to clipboard!')}`,
        type: 'success',
        code: 0,
      }),
    );
  };

  /**
   * Open modal to expand JSON code viewer
   * @param {string} title - Modal title
   * @param {any} data - JSON data to display
   */
  const handleExpandModal = (title: string, data: any): void => {
    setExpandedModal({
      isOpen: true,
      title,
      data,
    });
  };

  /**
   * Fetches the idTag reserved for the next super-admin remote start on a charging point
   * and stores it for display. The value is stable until a remote start actually consumes it.
   * @param {string} chargingPointSn - The charging point serial number
   * @returns {Promise<void>} Resolves when the idTag has been fetched
   */
  const fetchIdTag = async (chargingPointSn: string): Promise<void> => {
    if (!chargingPointSn) return;
    setIdTagInfo((previous) => ({ ...previous, loading: true }));
    try {
      const response = await ocppEvents.fetchIdTag(chargingPointSn);
      const idTag = response?.data?.idTag || '';
      setIdTagInfo({ value: idTag, loading: false, fetchedAt: idTag ? Date.now() : null });
    } catch (error) {
      console.error(error);
      setIdTagInfo({ value: '', loading: false, fetchedAt: null });
    }
  };

  /**
   * Fetches whether a non-terminal firmware job currently exists for the
   * selected charging point, for display in the Update Firmware action card.
   * @param {string} chargingPointSn - The charging point serial number
   * @returns {Promise<void>} Resolves when the pending job status has been fetched
   */
  const fetchPendingFirmwareJob = async (chargingPointSn: string): Promise<void> => {
    if (!chargingPointSn || chargingPointSn === '-') return;
    setPendingFirmwareJob((previous) => ({ ...previous, loading: true }));
    try {
      const response = await deviceHubApi.fetchPendingFirmwareJob(chargingPointSn);
      const data = response?.data || {};
      setPendingFirmwareJob({
        loading: false,
        hasPendingJob: Boolean(data.hasPendingJob),
        firmwareJob: data.firmwareJob || null,
      });
    } catch (error) {
      console.error('Error fetching pending firmware job:', error);
      setPendingFirmwareJob({ loading: false, hasPendingJob: false, firmwareJob: null });
    }
  };

  /**
   * Cancels the pending firmware job for the selected charging point. This
   * only marks our record as cancelled — it cannot stop a charger that is
   * already downloading/installing, since OCPP 1.6J has no cancellation
   * message for UpdateFirmware.
   * @returns {Promise<void>} Resolves when the cancel request completes
   */
  const handleCancelFirmwareJob = async (): Promise<void> => {
    const jobId = pendingFirmwareJob.firmwareJob?._id;
    if (!jobId) {
      setShowCancelFirmwareModal(false);
      return;
    }
    setCancellingFirmwareJob(true);
    try {
      await deviceHubApi.cancelFirmwareJob(jobId, 'Cancelled by operator from portal');
      setShowCancelFirmwareModal(false);
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('firmwareJobCancelled', 'Firmware job cancelled.'),
          type: 'success',
          code: 0,
        }),
      );
      fetchPendingFirmwareJob(deviceSummary.chargingPointSn);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message || t('somethingWentWrong', 'Something went wrong!'),
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setCancellingFirmwareJob(false);
    }
  };

  /**
   * Fetches whether a non-terminal diagnostics request currently exists for
   * the selected charging point, for display in the Get Diagnostics action card.
   * @param {string} chargingPointSn - The charging point serial number
   * @returns {Promise<void>} Resolves when the pending request status has been fetched
   */
  const fetchPendingDiagnosticsRequest = async (chargingPointSn: string): Promise<void> => {
    if (!chargingPointSn || chargingPointSn === '-') return;
    setPendingDiagnosticsRequest((previous) => ({ ...previous, loading: true }));
    try {
      const response = await diagnosticFirmwareApi.fetchPendingDiagnosticsRequest(chargingPointSn);
      const data = response?.data || {};
      setPendingDiagnosticsRequest({
        loading: false,
        hasPendingRequest: Boolean(data.hasPendingRequest),
        diagnosticsRequest: data.diagnosticsRequest || null,
      });
    } catch (error) {
      console.error('Error fetching pending diagnostics request:', error);
      setPendingDiagnosticsRequest({ loading: false, hasPendingRequest: false, diagnosticsRequest: null });
    }
  };

  /**
   * Cancels the pending diagnostics request for the selected charging point.
   * This only marks our record as cancelled — it cannot stop a charger that
   * is already uploading, since OCPP 1.6 has no cancellation message for
   * GetDiagnostics.
   * @returns {Promise<void>} Resolves when the cancel request completes
   */
  const handleCancelDiagnosticsRequest = async (): Promise<void> => {
    const requestId = pendingDiagnosticsRequest.diagnosticsRequest?._id;
    if (!requestId) {
      setShowCancelDiagnosticsModal(false);
      return;
    }
    setCancellingDiagnosticsRequest(true);
    try {
      await diagnosticFirmwareApi.cancelDiagnosticsRequest(
        requestId,
        'Cancelled by operator from portal',
      );
      setShowCancelDiagnosticsModal(false);
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('diagnosticsRequestCancelled', 'Diagnostics request cancelled.'),
          type: 'success',
          code: 0,
        }),
      );
      fetchPendingDiagnosticsRequest(deviceSummary.chargingPointSn);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message || t('somethingWentWrong', 'Something went wrong!'),
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setCancellingDiagnosticsRequest(false);
    }
  };

  /**
   * Function to handle api call for submitting the command
   * @param {string} type - The OCPP command type
   * @param {any} requestBody - The request body payload
   * @returns {Promise<void>} Resolves when API call completes
   */
  const handleApiCall = async (type: string, requestBody: any): Promise<void> => {
    setFormStatus({
      buttonLoading: true,
    });

    const cleanedBody = Object.fromEntries(
      Object.entries(requestBody).filter(([, value]) => value !== null && value !== ''),
    );

    if (pollingIntervalReference.current) {
      clearInterval(pollingIntervalReference.current);
      pollingIntervalReference.current = null;
    }
    if (pollingTimeoutReference.current) {
      clearTimeout(pollingTimeoutReference.current);
      pollingTimeoutReference.current = null;
    }

    setOcppConsoleLogs({
      request: null,
      response: null,
      isLoadingResponse: true,
    });

    try {
      const apiResponse = await ocppEvents.anyCommand({
        chargingPointSns: [selectedChargingPoint?.chargingPointSn],
        type,
        body: cleanedBody,
      });

      if (onActionTriggered) {
        onActionTriggered();
      }

      if (type === ocppMessageType.RMT_START_TRANSACTION) {
        fetchIdTag(selectedChargingPoint?.chargingPointSn || deviceSummary.chargingPointSn);
      } else if (type === ocppMessageType.UPDATE_FIRMWARE) {
        fetchPendingFirmwareJob(selectedChargingPoint?.chargingPointSn || deviceSummary.chargingPointSn);
      } else if (type === ocppMessageType.GET_DIAGNOSTICS) {
        fetchPendingDiagnosticsRequest(
          selectedChargingPoint?.chargingPointSn || deviceSummary.chargingPointSn,
        );
      }

      setModalView({
        status: true,
        title: '',
        content: (
          <SuccessNotification
            title={t('commandSubmittedSuccessfully', 'Command submitted successfully')}
            description={t(
              'commandSubmittedSuccessfullyDesc',
              'Your command has been submitted successfully.',
            )}
            notificationCloseTimer={3000}
            onNotificationClose={() => setModalView({ status: false, title: '', content: <div /> })}
          />
        ),
      });

      let chargingPointSn = selectedChargingPoint?.chargingPointSn || '';
      let messageId = '';

      /**
       * Recursively extracts chargingPointSn and messageId from any response shape
       * @param {any} source - The source payload
       */
      const extractIdentifiers = (source: any): void => {
        if (!source) return;

        if (typeof source === 'string') {
          try {
            const parsed = JSON.parse(source);
            extractIdentifiers(parsed);
          } catch {
            // Non-JSON string
          }
          return;
        }

        if (Array.isArray(source)) {
          source.forEach((item: any) => {
            extractIdentifiers(item);
          });
          return;
        }

        if (typeof source === 'object') {
          Object.keys(source).forEach((key) => {
            const lowerKey = key.toLowerCase().replace(/[-_]/g, '');
            const value = source[key];

            if (
              (lowerKey.includes('chargingpointsn') ||
                lowerKey.includes('serialnumber') ||
                lowerKey === 'sn' ||
                lowerKey === 'devicesn') &&
              typeof value === 'string' &&
              value
            ) {
              chargingPointSn = value;
            }

            if (
              (lowerKey.includes('messageid') ||
                lowerKey.includes('uniqueid') ||
                lowerKey === 'msgid' ||
                lowerKey === 'id' ||
                lowerKey === 'transactionid' ||
                lowerKey === 'correlationid') &&
              (typeof value === 'string' || typeof value === 'number') &&
              value
            ) {
              messageId = String(value);
            }

            if (value && typeof value === 'object') {
              extractIdentifiers(value);
            }
          });
        }
      };

      const commandsList =
        apiResponse?.data?.commands || apiResponse?.data?.data?.commands || apiResponse?.commands;

      if (Array.isArray(commandsList) && commandsList.length > 0) {
        const firstCommand = commandsList[0];
        if (firstCommand?.chargingPointSn) {
          chargingPointSn = firstCommand.chargingPointSn;
        }
        if (firstCommand?.messageId) {
          messageId = firstCommand.messageId;
        }
      }

      extractIdentifiers(apiResponse);

      if (!chargingPointSn && selectedChargingPoint?.chargingPointSn) {
        chargingPointSn = selectedChargingPoint.chargingPointSn;
      }

      if (chargingPointSn && messageId) {
        let attemptsCount = 0;
        const maxAttempts = 10;

        /**
         * Fetches OCPP message logs by chargingPointSn and messageId
         * @returns {Promise<boolean>} True if response was fetched, false otherwise
         */
        const fetchMessageLogsByMessageId = async (): Promise<boolean> => {
          attemptsCount += 1;
          try {
            const logsResponse = await ocppEvents.fetchMessageLogs({
              chargingPointSn,
              messageId,
            });

            const logData =
              logsResponse?.data?.data ||
              logsResponse?.data?.result ||
              logsResponse?.data ||
              logsResponse ||
              {};

            let extractedRequest = logData.request !== undefined ? logData.request : null;
            let extractedResponse = logData.response !== undefined ? logData.response : null;

            if (Array.isArray(logData)) {
              const foundRequest = logData.find(
                (item: any) =>
                  item.type === 'out' ||
                  item.request ||
                  (Array.isArray(item.message) && item.message[0] === 2),
              );
              const foundResponse = logData.find(
                (item: any) =>
                  item.type === 'in' ||
                  item.response ||
                  (Array.isArray(item.message) && item.message[0] === 3),
              );
              if (foundRequest) {
                extractedRequest = foundRequest.request || foundRequest.message || foundRequest;
              }
              if (foundResponse) {
                extractedResponse =
                  foundResponse.response || foundResponse.message || foundResponse;
              }
            }

            if (typeof extractedRequest === 'string') {
              try {
                extractedRequest = JSON.parse(extractedRequest);
              } catch {
                // Keep raw string if parse fails
              }
            }
            if (typeof extractedResponse === 'string') {
              try {
                extractedResponse = JSON.parse(extractedResponse);
              } catch {
                // Keep raw string if parse fails
              }
            }

            if (extractedRequest || extractedResponse) {
              setOcppConsoleLogs((previousState) => ({
                request: extractedRequest !== null ? extractedRequest : previousState.request,
                response: extractedResponse !== null ? extractedResponse : previousState.response,
                isLoadingResponse: !extractedResponse,
              }));

              if (extractedResponse) {
                if (pollingIntervalReference.current) {
                  clearInterval(pollingIntervalReference.current);
                  pollingIntervalReference.current = null;
                }
                if (pollingTimeoutReference.current) {
                  clearTimeout(pollingTimeoutReference.current);
                  pollingTimeoutReference.current = null;
                }
                return true;
              }
            }

            if (attemptsCount >= maxAttempts) {
              if (pollingIntervalReference.current) {
                clearInterval(pollingIntervalReference.current);
                pollingIntervalReference.current = null;
              }
              if (pollingTimeoutReference.current) {
                clearTimeout(pollingTimeoutReference.current);
                pollingTimeoutReference.current = null;
              }
              setOcppConsoleLogs((previousState) => ({
                ...previousState,
                isLoadingResponse: false,
              }));
              return true;
            }

            return false;
          } catch (error) {
            console.error('Error fetching message logs:', error);
            if (attemptsCount >= maxAttempts) {
              if (pollingIntervalReference.current) {
                clearInterval(pollingIntervalReference.current);
                pollingIntervalReference.current = null;
              }
              if (pollingTimeoutReference.current) {
                clearTimeout(pollingTimeoutReference.current);
                pollingTimeoutReference.current = null;
              }
              setOcppConsoleLogs((previousState) => ({
                ...previousState,
                isLoadingResponse: false,
              }));
              return true;
            }
            return false;
          }
        };

        const isCompleted = await fetchMessageLogsByMessageId();
        if (!isCompleted) {
          pollingIntervalReference.current = setInterval(fetchMessageLogsByMessageId, 5000);
        }
      } else {
        setOcppConsoleLogs((previousState) => ({
          ...previousState,
          isLoadingResponse: false,
        }));
      }
    } catch (error: any) {
      setOcppConsoleLogs((previousState) => ({
        ...previousState,
        isLoadingResponse: false,
        response: error?.response?.data || {
          message: error?.message || t('somethingWentWrong', 'Something went wrong!'),
        },
      }));
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.data?.message || t('somethingWentWrong', 'Something went wrong!'),
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setFormStatus({
        buttonLoading: false,
      });
    }
  };

  /**
   * Handles submitting the active command with parameters. Date-picker
   * values are collected in the browser's local timezone and are converted
   * to UTC before being sent to the any-command API.
   * @returns {Promise<void>} Resolves when command is submitted
   */
  const handleSubmitCommand = async (): Promise<void> => {
    setFormStatus({
      buttonLoading: true,
    });
    const currentActiveCommand = availableCommands.find((command) => command.isActive);
    if (!currentActiveCommand) {
      setFormStatus({
        buttonLoading: false,
      });
      return;
    }

    const missingRequiredInput = currentActiveCommand.inputs.find(
      (item) =>
        item.valueRequired &&
        (item.value === '' || item.value === null || item.value === undefined) &&
        !(item.key === 'scheduledRetrieveDate' && firmwareScheduleMode === 'immediate'),
    );
    if (missingRequiredInput) {
      setFormStatus({
        buttonLoading: false,
      });
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('fieldIsRequired', '{{field}} is required.', {
            field: missingRequiredInput.label,
          }),
          type: 'error',
          code: 0,
        }),
      );
      return;
    }

    const startTimeInput = currentActiveCommand.inputs.find((item) => item.key === 'startTime');
    const startTimeValueForSubmit = startTimeInput?.value as string | undefined;
    let startTimeValidationMessage = '';
    if (isFutureDateTime(startTimeValueForSubmit)) {
      startTimeValidationMessage = t(
        'startTimeCannotBeFuture',
        'Include Logs From cannot be a future date/time.',
      );
    } else if (isBeforeMinDate(startTimeValueForSubmit, getStartTimeMinDateString())) {
      startTimeValidationMessage = t(
        'startTimeTooFarInPast',
        'Include Logs From cannot be more than 3 months in the past.',
      );
    }
    if (startTimeValidationMessage) {
      setFormStatus({
        buttonLoading: false,
      });
      setStartTimeError(startTimeValidationMessage);
      dispatch(
        setNotificationMessage({
          status: true,
          message: startTimeValidationMessage,
          type: 'error',
          code: 0,
        }),
      );
      return;
    }

    const stopTimeInput = currentActiveCommand.inputs.find((item) => item.key === 'stopTime');
    if (stopTimeInput?.value) {
      let stopTimeValidationMessage = '';
      if (isFutureDateTime(stopTimeInput.value as string)) {
        stopTimeValidationMessage = t(
          'stopTimeCannotBeFuture',
          'Include Logs Until cannot be a future date/time.',
        );
      } else if (
        startTimeInput?.value &&
        (stopTimeInput.value as string) <= (startTimeInput.value as string)
      ) {
        stopTimeValidationMessage = t(
          'stopTimeMustBeAfterStartTime',
          'Include Logs Until must be later than Include Logs From.',
        );
      }
      if (stopTimeValidationMessage) {
        setFormStatus({
          buttonLoading: false,
        });
        setStopTimeError(stopTimeValidationMessage);
        dispatch(
          setNotificationMessage({
            status: true,
            message: stopTimeValidationMessage,
            type: 'error',
            code: 0,
          }),
        );
        return;
      }
    }

    const requestBody: any = currentActiveCommand.inputs.reduce(
      (accumulator, item) => {
        if (item.type === 'number' || item.key === 'connectorId') {
          accumulator[item.key] = item.value === '' ? '' : Number(item.value);
        } else if (item.sendAsList) {
          accumulator[item.key] = item.value ? [item.value] : [];
        } else if (item.type === 'date-picker') {
          // Date pickers collect a wall-clock value in the browser's local
          // timezone; the API expects UTC, so convert before sending.
          const dateTimeValue =
            item.key === 'scheduledRetrieveDate' && firmwareScheduleMode === 'immediate'
              ? getImmediateFirmwareRetrieveDateTime()
              : (item.value as string);
          accumulator[item.key] = dateTimeValue ? ConvertDateTimeToUTC(dateTimeValue) : '';
        } else {
          accumulator[item.key] = item.value;
        }
        return accumulator;
      },
      {} as { [key: string]: string | number | (string | number)[] },
    );

    if (currentActiveCommand.type === ocppMessageType.CHANGE_CONFIGURATION_CUSTOM) {
      const finalRequestBody: any = {
        key: requestBody.key,
      };

      // By default string
      finalRequestBody.value = requestBody.value.toString();

      if (requestBody.inputType === 'boolean') {
        finalRequestBody.value = Boolean(requestBody.value);
      }

      await handleApiCall(ocppMessageType.CHANGE_CONFIGURATION, finalRequestBody);
      return;
    }

    await handleApiCall(currentActiveCommand.type, requestBody);
  };

  useEffect(() => {
    /**
     * Fetch device details and connectors for the selected device
     * @returns {Promise<void>} Resolves after device data is fetched
     */
    const fetchDeviceData = async (): Promise<void> => {
      const activeId = id || selectedChargingPoint?.id;
      if (!activeId) {
        if (selectedChargingPoint) {
          setDeviceSummary((previous) => ({
            ...previous,
            model: previous.model || selectedChargingPoint.modelSeries || '',
            chargingPointSn:
              previous.chargingPointSn ||
              selectedChargingPoint.chargingPointSn ||
              selectedChargingPoint.name ||
              '',
          }));
        }
        return;
      }
      try {
        let connectorList: any[] = [];
        let brandValue = '';
        let modelValue = selectedChargingPoint?.modelSeries || '';
        let snValue = selectedChargingPoint?.chargingPointSn || selectedChargingPoint?.name || '';
        let firmwareValue = '';
        let tariffValue = '';

        if (isDeviceHub) {
          const response = await deviceHubApi.fetchAdvanceDeviceDetails(activeId);
          const data = response?.data || response || {};
          connectorList = data.connectorInformation || data.connectors || [];
          brandValue =
            data.brandName || data.vendor || data.chargePointVendor || data.chargerBrand || '';
          modelValue =
            data.modelName || data.chargePointModel || data.model || data.modelSeries || modelValue;
          snValue = data.serialNumber || data.chargingPointSn || data.chargingPointName || snValue;
          firmwareValue =
            data.firmwareVersion || data.specifications?.firmwareVersion || data.firmware || '';
          const connectorTariffs = connectorList
            .map(
              (conn: any) =>
                conn.tariffName ||
                conn.tariff?.tariffName ||
                conn.tariff?.name ||
                conn.tariffId ||
                conn.tariff?.tariffId ||
                (typeof conn.tariff === 'string' ? conn.tariff : ''),
            )
            .filter(Boolean);
          const uniqueTariffs = Array.from(new Set(connectorTariffs));
          tariffValue =
            data.tariffName ||
            data.tariff?.tariffName ||
            data.tariff?.name ||
            data.tariffId ||
            data.tariff?.tariffId ||
            (typeof data.tariff === 'string' ? data.tariff : '') ||
            (uniqueTariffs.length > 0 ? uniqueTariffs.join(', ') : '');
        } else {
          const response = await device.fetchChargingPointGeneralDetails(activeId);
          const data = response?.data || {};
          const enelData = data.enelData || {};
          connectorList = data.connectors || data.connectorInformation || [];
          brandValue =
            data.chargePointVendor || data.brandName || data.vendor || data.chargerBrand || '';
          modelValue =
            data.chargePointModel || data.modelName || data.model || data.modelSeries || modelValue;
          snValue =
            data.chargingPointSn ||
            data.serialNumber ||
            data.chargingPointName ||
            (enelData.sn !== undefined ? String(enelData.sn) : '') ||
            snValue;
          firmwareValue = data.firmwareVersion || data.firmware || '';
          const connectorTariffs = connectorList
            .map(
              (conn: any) =>
                conn.tariffName ||
                conn.tariff?.tariffName ||
                conn.tariff?.name ||
                conn.tariffId ||
                conn.tariff?.tariffId ||
                (typeof conn.tariff === 'string' ? conn.tariff : ''),
            )
            .filter(Boolean);
          const uniqueTariffs = Array.from(new Set(connectorTariffs));
          tariffValue =
            data.tariffName ||
            data.tariff?.tariffName ||
            data.tariff?.name ||
            data.tariffId ||
            data.tariff?.tariffId ||
            (typeof data.tariff === 'string' ? data.tariff : '') ||
            (uniqueTariffs.length > 0 ? uniqueTariffs.join(', ') : '');
        }

        setDeviceSummary({
          brand: brandValue || '-',
          model: modelValue || '-',
          chargingPointSn: snValue || '-',
          firmware: firmwareValue || '-',
          tariff: tariffValue || '-',
        });

        const options: OcppCommandInputOption[] = connectorList.map((conn: any, index: number) => {
          const connectorId =
            conn.connectorId !== undefined && conn.connectorId !== null
              ? conn.connectorId
              : index + 1;
          return {
            label: `${t('connector')} ${connectorId}`,
            value: String(connectorId),
          };
        });

        setConnectorOptions(options);
      } catch (error) {
        console.error('Error fetching device data:', error);
      }
    };

    fetchDeviceData();
  }, [id, selectedChargingPoint, t]);

  useEffect(() => {
    /**
     * Fetches firmware artifacts for the selected charging point.
     */
    const fetchFirmwareOptions = async () => {
      if (deviceSummary.chargingPointSn && deviceSummary.chargingPointSn !== '-') {
        try {
          const response = await deviceHubApi.fetchAvailableFirmwareArtifacts(
            deviceSummary.chargingPointSn,
          );
          const artifacts = response?.data?.data || response?.data || response || [];
          const options = Array.isArray(artifacts)
            ? artifacts.map((artifact: any) => ({
                label: artifact.version || artifact.name || artifact.url,
                value: artifact.id || artifact._id || artifact.url || artifact.downloadUrl,
              }))
            : [];
          setFirmwareOptions(options);
        } catch (error) {
          console.error('Error fetching firmware artifacts:', error);
        }
      }
    };
    fetchFirmwareOptions();
  }, [deviceSummary.chargingPointSn]);

  useEffect(() => {
    setAvailableCommands((previousCommands) =>
      previousCommands.map((command) => ({
        ...command,
        inputs: command.inputs.map((input) => {
          if (input.key === 'connectorId') {
            return {
              ...input,
              type: 'select-field',
              options: connectorOptions,
            };
          }
          if (input.key === 'artifactId') {
            return {
              ...input,
              type: 'select-field',
              options: firmwareOptions,
            };
          }
          if (input.key === 'protocol') {
            return {
              ...input,
              type: 'select-field',
              options: protocolOptions,
            };
          }
          return input;
        }),
      })),
    );
  }, [connectorOptions, firmwareOptions, protocolOptions]);

  useEffect(() => {
    const foundActiveCommand: OcppCommandProperties | undefined = availableCommands.find(
      (command) => command.isActive && !command.disableCommand,
    );
    if (foundActiveCommand) {
      setActiveCommands(foundActiveCommand);
    }
  }, [availableCommands]);

  useEffect(() => {
    if (
      activeCommand?.type === ocppMessageType.RMT_START_TRANSACTION &&
      deviceSummary.chargingPointSn
    ) {
      fetchIdTag(deviceSummary.chargingPointSn);
    }
  }, [activeCommand?.type, deviceSummary.chargingPointSn]);

  useEffect(() => {
    if (
      activeCommand?.type === ocppMessageType.UPDATE_FIRMWARE &&
      deviceSummary.chargingPointSn &&
      deviceSummary.chargingPointSn !== '-'
    ) {
      fetchPendingFirmwareJob(deviceSummary.chargingPointSn);
    }
  }, [activeCommand?.type, deviceSummary.chargingPointSn]);

  useEffect(() => {
    if (
      activeCommand?.type !== ocppMessageType.UPDATE_FIRMWARE ||
      !deviceSummary.chargingPointSn ||
      deviceSummary.chargingPointSn === '-'
    ) {
      return undefined;
    }
    const interval = setInterval(() => {
      fetchPendingFirmwareJob(deviceSummary.chargingPointSn);
    }, 30000);
    return () => clearInterval(interval);
  }, [activeCommand?.type, deviceSummary.chargingPointSn]);

  useEffect(() => {
    if (
      activeCommand?.type === ocppMessageType.GET_DIAGNOSTICS &&
      deviceSummary.chargingPointSn &&
      deviceSummary.chargingPointSn !== '-'
    ) {
      fetchPendingDiagnosticsRequest(deviceSummary.chargingPointSn);
    }
  }, [activeCommand?.type, deviceSummary.chargingPointSn]);

  useEffect(() => {
    if (
      activeCommand?.type !== ocppMessageType.GET_DIAGNOSTICS ||
      !deviceSummary.chargingPointSn ||
      deviceSummary.chargingPointSn === '-'
    ) {
      return undefined;
    }
    const interval = setInterval(() => {
      fetchPendingDiagnosticsRequest(deviceSummary.chargingPointSn);
    }, 30000);
    return () => clearInterval(interval);
  }, [activeCommand?.type, deviceSummary.chargingPointSn]);

  useEffect(() => {
    /**
     * Fetches the transfer protocols supported by the selected charging
     * point for whichever of Get Diagnostics / Update Firmware is active.
     */
    const fetchProtocolOptions = async (): Promise<void> => {
      let protocolQueryType: 'diagnostics' | 'firmware' | null = null;

      if (activeCommand?.type === ocppMessageType.GET_DIAGNOSTICS) {
        protocolQueryType = 'diagnostics';
      } else if (activeCommand?.type === ocppMessageType.UPDATE_FIRMWARE) {
        protocolQueryType = 'firmware';
      }

      if (
        !protocolQueryType ||
        !deviceSummary.chargingPointSn ||
        deviceSummary.chargingPointSn === '-'
      ) {
        setProtocolOptions([]);
        return;
      }

      try {
        const response = await deviceHubApi.fetchSupportedProtocols(
          deviceSummary.chargingPointSn,
          protocolQueryType,
        );
        const protocols = response?.data?.protocols || response?.data?.data?.protocols || [];
        const options = Array.isArray(protocols)
          ? protocols.map((protocol: string) => ({
              label: protocol.toUpperCase(),
              value: protocol,
            }))
          : [];
        setProtocolOptions(options);
      } catch (error) {
        console.error('Error fetching supported protocols:', error);
        setProtocolOptions([]);
      }
    };
    fetchProtocolOptions();
  }, [activeCommand?.type, deviceSummary.chargingPointSn]);

  useEffect(() => {
    const interval = setInterval(() => forceIdTagTick((tick) => tick + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const operation = searchParameters.get('operation');
    const resetType = searchParameters.get('resetType');
    if (operation) {
      setAvailableCommands((previousCommands) =>
        previousCommands.map((command) => {
          const isActive = command.type === operation;
          if (isActive && resetType && command.type === ocppMessageType.RESET_CP) {
            const updatedInputs = command.inputs.map((input) => {
              if (input.key === 'resetType') {
                return {
                  ...input,
                  value: resetType,
                };
              }
              return input;
            });
            return {
              ...command,
              isActive,
              inputs: updatedInputs,
            };
          }
          return {
            ...command,
            isActive,
          };
        }),
      );
    }
  }, [searchParameters]);

  const idTagMinutesRemaining = getIdTagMinutesRemaining(idTagInfo.fetchedAt);
  const isIdTagExpired = Boolean(idTagInfo.fetchedAt) && idTagMinutesRemaining <= 0;

  return (
    <div className="device-ocpp-operations-wrap">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView({ ...modalView, status: false })}
        header={false}
        rightCorner=""
        notification
      />
      <div className="ocpp-device-summary-bar">
        <div className="ocpp-summary-item">
          <span className="summary-label">{t('chargerBrand')}</span>
          <span className="summary-value" title={deviceSummary.brand || '-'}>
            {deviceSummary.brand || '-'}
          </span>
        </div>
        <div className="ocpp-summary-divider" />
        <div className="ocpp-summary-item">
          <span className="summary-label">{t('deviceModel')}</span>
          <span className="summary-value" title={deviceSummary.model || '-'}>
            {deviceSummary.model || '-'}
          </span>
        </div>
        <div className="ocpp-summary-divider" />
        <div className="ocpp-summary-item">
          <span className="summary-label">{t('chargingPointSn', 'Charging Point SN')}</span>
          <span className="summary-value" title={deviceSummary.chargingPointSn || '-'}>
            {deviceSummary.chargingPointSn || '-'}
          </span>
        </div>
        <div className="ocpp-summary-divider" />
        <div className="ocpp-summary-item">
          <span className="summary-label">{t('firmwareVersion', 'Firmware')}</span>
          <span className="summary-value" title={deviceSummary.firmware || '-'}>
            {deviceSummary.firmware || '-'}
          </span>
        </div>
        {!isDeviceHub && (
          <>
            <div className="ocpp-summary-divider" />
            <div className="ocpp-summary-item">
              <span className="summary-label">{t('tariff', 'Tariff')}</span>
              <span className="summary-value" title={deviceSummary.tariff || '-'}>
                {deviceSummary.tariff || '-'}
              </span>
            </div>
          </>
        )}
      </div>
      <div className={`device-ocpp-operations-container ${hideSidebar ? 'hide-sidebar' : ''}`}>
        {!hideSidebar && (
          <div className="ocpp-operations-sidebar-wrap">
            {availableCommands
              .filter((command) => !command.disableCommand)
              .map((command) => (
                <div
                  key={command.type}
                  className={`sidebar-each-command-wrap ${command.isActive ? 'active-command' : ''}`}
                  role="presentation"
                  onClick={() => handleChangeActiveMenu(command.type)}>
                  {renderCommandSidebarIcon(command.type)}
                  <h6>{command.name}</h6>
                </div>
              ))}
          </div>
        )}
        <div className="ocpp-operations-content-wrap">
          <div className="ocpp-operations-header">
            <h2 className="ocpp-operations-title">{activeCommand?.name}</h2>
            {activeCommand?.description && (
              <p className="ocpp-operations-description">{activeCommand.description}</p>
            )}
          </div>

          {activeCommand?.whatHappens && activeCommand.whatHappens.length > 0 && (
            <div className="ocpp-what-happens-banner">
              <div className="what-happens-content">
                {renderInfoIcon('what-happens-icon')}
                <div className="what-happens-text-wrap">
                  {activeCommand.whatHappens.map((point) => (
                    <p key={point} className="what-happens-line">
                      {point}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="ocpp-action-card">
            <h5 className="action-card-title">{t('action', 'Action')}</h5>
            <div className="action-card-body">
              {(() => {
                if (activeCommand?.type === ocppMessageType.SET_CHARGING_PROFILE) {
                  return (
                    <SetChargingProfile
                      handleApiCall={handleApiCall}
                      buttonLoading={formStatus.buttonLoading}
                    />
                  );
                }
                if (activeCommand?.inputs?.length === 0) {
                  return (
                    <div className="action-no-params-row">
                      <span className="action-no-params-text">
                        {t(
                          'commandNoParamsRequired',
                          'This command does not require any input parameters.',
                        )}
                      </span>
                      <div className="action-submit-btn-wrap">
                        <MyButton
                          label={t('submit', 'Submit')}
                          buttonType="submit"
                          loading={formStatus.buttonLoading}
                          disableButton={formStatus.buttonLoading}
                          onClickFunc={handleSubmitCommand}
                        />
                      </div>
                    </div>
                  );
                }
                return (
                  <>
                    {activeCommand?.type === ocppMessageType.RMT_START_TRANSACTION && (
                      <div className="ocpp-id-tag-display">
                        <div className="id-tag-info">
                          <span className="id-tag-label">
                            {t('sessionIdTag', 'Session ID / idTag')}
                          </span>
                          <span className="id-tag-value">
                            {idTagInfo.loading
                              ? t('loading', 'Loading...')
                              : idTagInfo.value || '-'}
                          </span>
                        </div>
                        <div className="id-tag-actions">
                          {!idTagInfo.loading && idTagInfo.value && (
                            <span
                              className={`id-tag-expiry-badge ${
                                isIdTagExpired ? 'expired' : 'valid'
                              }`}>
                              {isIdTagExpired
                                ? t('expired', 'Expired')
                                : t('expiresIn', 'Expires in {{duration}}', {
                                    duration: `${idTagMinutesRemaining}m`,
                                  })}
                            </span>
                          )}
                          <span
                            className="id-tag-refresh-btn"
                            role="presentation"
                            title={t('refresh', 'Refresh')}
                            onClick={() => fetchIdTag(deviceSummary.chargingPointSn)}>
                            <RefreshIcon />
                          </span>
                        </div>
                      </div>
                    )}
                    {activeCommand?.type === ocppMessageType.UPDATE_FIRMWARE && (
                      <div className="ocpp-firmware-job-card">
                        <div className="firmware-job-card-header">
                          <div className="firmware-job-card-title">
                            <UpdateFirmwareIcon
                              className={`firmware-job-card-icon ${
                                !pendingFirmwareJob.hasPendingJob ? 'muted' : ''
                              }`}
                            />
                            <h6>{t('pendingFirmwareUpdate', 'Pending Firmware Update')}</h6>
                          </div>
                          <div className="firmware-job-card-header-actions">
                            {pendingFirmwareJob.hasPendingJob && pendingFirmwareJob.firmwareJob && (
                              <span
                                className={`firmware-job-status-badge status-${(
                                  pendingFirmwareJob.firmwareJob.status || ''
                                )
                                  .toLowerCase()
                                  .replace(/\s+/g, '-')}`}>
                                {pendingFirmwareJob.firmwareJob.status}
                              </span>
                            )}
                            <span
                              className={`firmware-job-refresh-btn ${
                                pendingFirmwareJob.loading ? 'spinning' : ''
                              }`}
                              role="presentation"
                              title={t('refresh', 'Refresh')}
                              onClick={() => fetchPendingFirmwareJob(deviceSummary.chargingPointSn)}>
                              <RefreshIcon />
                            </span>
                          </div>
                        </div>
                        {pendingFirmwareJob.loading && (
                          <span className="firmware-job-status-text">
                            {t('loading', 'Loading...')}
                          </span>
                        )}
                        {!pendingFirmwareJob.loading && !pendingFirmwareJob.hasPendingJob && (
                          <span className="firmware-job-status-text">
                            {t(
                              'noPendingFirmwareJob',
                              'No pending firmware job for this charger.',
                            )}
                          </span>
                        )}
                        {!pendingFirmwareJob.loading &&
                          pendingFirmwareJob.hasPendingJob &&
                          pendingFirmwareJob.firmwareJob && (
                            <>
                              <div className="firmware-job-card-grid">
                                <div className="firmware-job-field">
                                  <span className="firmware-job-field-label">
                                    {t('protocol', 'Protocol')}
                                  </span>
                                  <span className="firmware-job-field-value">
                                    {pendingFirmwareJob.firmwareJob.transferScheme?.toUpperCase() ||
                                      '-'}
                                  </span>
                                </div>
                                <div className="firmware-job-field">
                                  <span className="firmware-job-field-label">
                                    {t('downloadDateTime', 'Download Date/Time')}
                                  </span>
                                  <span className="firmware-job-field-value">
                                    {ConvertUTCtoDateTime(
                                      pendingFirmwareJob.firmwareJob.scheduledRetrieveDate,
                                    ) || '-'}
                                  </span>
                                </div>
                                <div className="firmware-job-field">
                                  <span className="firmware-job-field-label">
                                    {t('sentAt', 'Sent At')}
                                  </span>
                                  <span className="firmware-job-field-value">
                                    {ConvertUTCtoDateTime(pendingFirmwareJob.firmwareJob.sentAt) ||
                                      '-'}
                                  </span>
                                </div>
                                <div className="firmware-job-field">
                                  <span className="firmware-job-field-label">
                                    {t('retries', 'Retries')}
                                  </span>
                                  <span className="firmware-job-field-value">
                                    {pendingFirmwareJob.firmwareJob.retries ?? '-'}
                                  </span>
                                </div>
                              </div>
                              <div className="firmware-job-card-footer">
                                <div className="firmware-job-warning-note">
                                  {renderInfoIcon('firmware-job-warning-icon')}
                                  <span>
                                    {t(
                                      'firmwareJobCancelWarning',
                                      'Cancelling only stops tracking on our side — the charger may still complete or fail the update independently.',
                                    )}
                                  </span>
                                </div>
                                <MyButton
                                  label={t('cancel', 'Cancel')}
                                  buttonType="error"
                                  onClickFunc={() => setShowCancelFirmwareModal(true)}
                                />
                              </div>
                            </>
                          )}
                      </div>
                    )}
                    {activeCommand?.type === ocppMessageType.GET_DIAGNOSTICS && (
                      <div className="ocpp-diagnostics-request-card">
                        <div className="diagnostics-request-card-header">
                          <div className="diagnostics-request-card-title">
                            <GetDiagnosticsIcon
                              className={`diagnostics-request-card-icon ${
                                !pendingDiagnosticsRequest.hasPendingRequest ? 'muted' : ''
                              }`}
                            />
                            <h6>
                              {t('pendingDiagnosticsRequest', 'Pending Diagnostics Request')}
                            </h6>
                          </div>
                          <div className="diagnostics-request-card-header-actions">
                            {pendingDiagnosticsRequest.hasPendingRequest &&
                              pendingDiagnosticsRequest.diagnosticsRequest && (
                                <span
                                  className={`diagnostics-request-status-badge status-${(
                                    pendingDiagnosticsRequest.diagnosticsRequest.status || ''
                                  )
                                    .toLowerCase()
                                    .replace(/\s+/g, '-')}`}>
                                  {pendingDiagnosticsRequest.diagnosticsRequest.status}
                                </span>
                              )}
                            <span
                              className={`diagnostics-request-refresh-btn ${
                                pendingDiagnosticsRequest.loading ? 'spinning' : ''
                              }`}
                              role="presentation"
                              title={t('refresh', 'Refresh')}
                              onClick={() =>
                                fetchPendingDiagnosticsRequest(deviceSummary.chargingPointSn)
                              }>
                              <RefreshIcon />
                            </span>
                          </div>
                        </div>
                        {pendingDiagnosticsRequest.loading && (
                          <span className="diagnostics-request-status-text">
                            {t('loading', 'Loading...')}
                          </span>
                        )}
                        {!pendingDiagnosticsRequest.loading &&
                          !pendingDiagnosticsRequest.hasPendingRequest && (
                            <span className="diagnostics-request-status-text">
                              {t(
                                'noPendingDiagnosticsRequest',
                                'No pending diagnostics request for this charger.',
                              )}
                            </span>
                          )}
                        {!pendingDiagnosticsRequest.loading &&
                          pendingDiagnosticsRequest.hasPendingRequest &&
                          pendingDiagnosticsRequest.diagnosticsRequest && (
                            <>
                              <div className="diagnostics-request-card-grid">
                                <div className="diagnostics-request-field">
                                  <span className="diagnostics-request-field-label">
                                    {t('transferScheme', 'Transfer Scheme')}
                                  </span>
                                  <span className="diagnostics-request-field-value">
                                    {pendingDiagnosticsRequest.diagnosticsRequest.transferScheme?.toUpperCase() ||
                                      '-'}
                                  </span>
                                </div>
                                <div className="diagnostics-request-field">
                                  <span className="diagnostics-request-field-label">
                                    {t('timeWindow', 'Time Window')}
                                  </span>
                                  <span className="diagnostics-request-field-value">
                                    {pendingDiagnosticsRequest.diagnosticsRequest.startTime &&
                                    pendingDiagnosticsRequest.diagnosticsRequest.stopTime
                                      ? `${ConvertUTCtoDateTime(
                                          pendingDiagnosticsRequest.diagnosticsRequest.startTime,
                                        )} – ${ConvertUTCtoDateTime(
                                          pendingDiagnosticsRequest.diagnosticsRequest.stopTime,
                                        )}`
                                      : '-'}
                                  </span>
                                </div>
                                <div className="diagnostics-request-field">
                                  <span className="diagnostics-request-field-label">
                                    {t('sentAt', 'Sent At')}
                                  </span>
                                  <span className="diagnostics-request-field-value">
                                    {ConvertUTCtoDateTime(
                                      pendingDiagnosticsRequest.diagnosticsRequest.sentAt,
                                    ) || '-'}
                                  </span>
                                </div>
                                <div className="diagnostics-request-field">
                                  <span className="diagnostics-request-field-label">
                                    {t('retries', 'Retries')}
                                  </span>
                                  <span className="diagnostics-request-field-value">
                                    {pendingDiagnosticsRequest.diagnosticsRequest.retries ?? '-'}
                                  </span>
                                </div>
                              </div>
                              <div className="diagnostics-request-card-footer">
                                <div className="diagnostics-request-warning-note">
                                  {renderInfoIcon('diagnostics-request-warning-icon')}
                                  <span>
                                    {t(
                                      'diagnosticsRequestCancelWarning',
                                      'Cancelling only stops tracking on our side — the charger may still upload a file for this request independently.',
                                    )}
                                  </span>
                                </div>
                                <MyButton
                                  label={t('cancel', 'Cancel')}
                                  buttonType="error"
                                  onClickFunc={() => setShowCancelDiagnosticsModal(true)}
                                />
                              </div>
                            </>
                          )}
                      </div>
                    )}
                    <div
                      className={`action-params-content ${
                        activeCommand?.inputs && activeCommand.inputs.length > 3
                          ? 'multi-line-params'
                          : 'single-row-params'
                      }`}>
                      <div className="action-inputs-grid">
                        {activeCommand?.inputs.map((eachInput) => {
                          let inputDisplay;
                          switch (eachInput.type) {
                            case 'number':
                            case 'text':
                              inputDisplay = (
                                <CustomInput
                                  key={eachInput.key}
                                  name={eachInput.key}
                                  inputLabel={eachInput.label}
                                  type={eachInput.type}
                                  placeholder={eachInput.label}
                                  value={eachInput.value}
                                  onChangeValue={(name: string, value: string | number) =>
                                    handleChangeValue(activeCommand.type, name, value)
                                  }
                                  valueRequired={eachInput.valueRequired}
                                  fontSize="small"
                                />
                              );
                              break;
                            case 'select-field':
                              inputDisplay = (
                                <SelectField
                                  key={eachInput.key}
                                  name={eachInput.key}
                                  inputLabel={eachInput.label}
                                  selectedValue={eachInput.value}
                                  placeholder={eachInput.label}
                                  onChangeValue={(name: string, value: string | number) =>
                                    handleChangeValue(activeCommand.type, name, value)
                                  }
                                  valueRequired={eachInput.valueRequired}
                                  options={eachInput.options || []}
                                  fontSize="small"
                                  description={(option) => option.description}
                                />
                              );
                              break;
                            case 'date-picker': {
                              const startTimeValue = activeCommand?.inputs.find(
                                (inputItem) => inputItem.key === 'startTime',
                              )?.value as string | undefined;

                              if (eachInput.key === 'scheduledRetrieveDate') {
                                inputDisplay = (
                                  <div key={eachInput.key} className="firmware-retrieve-date-field">
                                    <p className="firmware-retrieve-date-label">
                                      {eachInput.label}
                                      {eachInput.valueRequired &&
                                        firmwareScheduleMode === 'schedule' && (
                                          <span className="required-asterisk">*</span>
                                        )}
                                    </p>
                                    <div className="firmware-schedule-mode-toggle">
                                      <button
                                        type="button"
                                        className={`firmware-schedule-mode-btn ${
                                          firmwareScheduleMode === 'schedule' ? 'active' : ''
                                        }`}
                                        onClick={() => setFirmwareScheduleMode('schedule')}>
                                        {t('scheduleForLater', 'Schedule for later')}
                                      </button>
                                      <button
                                        type="button"
                                        className={`firmware-schedule-mode-btn ${
                                          firmwareScheduleMode === 'immediate' ? 'active' : ''
                                        }`}
                                        onClick={() => setFirmwareScheduleMode('immediate')}>
                                        {t('startImmediately', 'Start immediately')}
                                      </button>
                                    </div>
                                    {firmwareScheduleMode === 'schedule' ? (
                                      <CustomDatePicker
                                        name={eachInput.key}
                                        value={eachInput.value as string}
                                        onChangeValue={(name: string, value: string) =>
                                          handleChangeValue(activeCommand.type, name, value)
                                        }
                                        valueRequired={eachInput.valueRequired}
                                        requiredTime
                                        fontSize="small"
                                        minDate={getTodayDateString()}
                                        disablePastTime
                                      />
                                    ) : (
                                      <p className="firmware-immediate-note">
                                        {t(
                                          'firmwareImmediateNote',
                                          'The firmware download will be scheduled to start within the next minute.',
                                        )}
                                      </p>
                                    )}
                                  </div>
                                );
                                break;
                              }

                              let dateInputMinDate: string | undefined;
                              if (eachInput.key === 'stopTime' && startTimeValue) {
                                [dateInputMinDate] = startTimeValue.split(' ');
                              }

                              const isDateRangeField =
                                eachInput.key === 'startTime' || eachInput.key === 'stopTime';

                              let dateInputErrorMessage: string | undefined;
                              if (eachInput.key === 'startTime') {
                                dateInputErrorMessage = startTimeError;
                              } else if (eachInput.key === 'stopTime') {
                                dateInputErrorMessage = stopTimeError;
                              }

                              inputDisplay = (
                                <CustomDatePicker
                                  key={eachInput.key}
                                  name={eachInput.key}
                                  inputLabel={eachInput.label}
                                  value={eachInput.value as string}
                                  onChangeValue={(name: string, value: string) => {
                                    if (name === 'startTime') {
                                      if (isFutureDateTime(value)) {
                                        setStartTimeError(
                                          t(
                                            'startTimeCannotBeFuture',
                                            'Include Logs From cannot be a future date/time.',
                                          ),
                                        );
                                      } else if (isBeforeMinDate(value, getStartTimeMinDateString())) {
                                        setStartTimeError(
                                          t(
                                            'startTimeTooFarInPast',
                                            'Include Logs From cannot be more than 3 months in the past.',
                                          ),
                                        );
                                      } else {
                                        setStartTimeError('');
                                      }
                                    } else if (name === 'stopTime') {
                                      if (isFutureDateTime(value)) {
                                        setStopTimeError(
                                          t(
                                            'stopTimeCannotBeFuture',
                                            'Include Logs Until cannot be a future date/time.',
                                          ),
                                        );
                                      } else if (value && startTimeValue && value <= startTimeValue) {
                                        setStopTimeError(
                                          t(
                                            'stopTimeMustBeAfterStartTime',
                                            'Include Logs Until must be later than Include Logs From.',
                                          ),
                                        );
                                      } else {
                                        setStopTimeError('');
                                      }
                                    }
                                    handleChangeValue(activeCommand.type, name, value);
                                  }}
                                  valueRequired={eachInput.valueRequired}
                                  requiredTime
                                  fontSize="small"
                                  minDate={dateInputMinDate}
                                  maxDate={isDateRangeField ? getTodayDateString() : undefined}
                                  disablePastTime={false}
                                  error={Boolean(dateInputErrorMessage)}
                                  errorMessage={dateInputErrorMessage}
                                />
                              );
                              break;
                            }
                            default:
                              inputDisplay = null;
                              break;
                          }
                          return inputDisplay;
                        })}
                      </div>
                      <div className="action-submit-btn-wrap">
                        <MyButton
                          label={t('submit', 'Submit')}
                          buttonType="submit"
                          loading={formStatus.buttonLoading}
                          disableButton={formStatus.buttonLoading}
                          onClickFunc={handleSubmitCommand}
                        />
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="ocpp-operations-json-logs-wrap">
            <div className="ocpp-json-card">
              <div className="ocpp-json-card-header">
                <h6>{t('request', 'Request')}</h6>
                <div className="ocpp-json-card-header-actions">
                  <button
                    type="button"
                    className="ocpp-json-action-btn"
                    disabled={!ocppConsoleLogs.request}
                    onClick={() =>
                      handleCopy(
                        JSON.stringify(ocppConsoleLogs.request, null, 2),
                        t('request', 'Request'),
                      )
                    }
                    title="Copy">
                    <CopyIcon />
                  </button>
                  <button
                    type="button"
                    className="ocpp-json-action-btn"
                    disabled={!ocppConsoleLogs.request}
                    onClick={() =>
                      handleExpandModal(t('request', 'Request'), ocppConsoleLogs.request)
                    }
                    title="Expand">
                    <ExpandIcon />
                  </button>
                </div>
              </div>
              <div className="ocpp-json-card-body">
                {ocppConsoleLogs.request ? (
                  <JsonCodeViewer data={ocppConsoleLogs.request} />
                ) : (
                  <div className="ocpp-json-empty-state">
                    <span>{t('noRequestSent', 'No request sent yet')}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="ocpp-json-card">
              <div className="ocpp-json-card-header">
                <h6>{t('response', 'Response')}</h6>
                <div className="ocpp-json-card-header-actions">
                  <button
                    type="button"
                    className="ocpp-json-action-btn"
                    disabled={!ocppConsoleLogs.response}
                    onClick={() =>
                      handleCopy(
                        JSON.stringify(ocppConsoleLogs.response, null, 2),
                        t('response', 'Response'),
                      )
                    }
                    title="Copy">
                    <CopyIcon />
                  </button>
                  <button
                    type="button"
                    className="ocpp-json-action-btn"
                    disabled={!ocppConsoleLogs.response}
                    onClick={() =>
                      handleExpandModal(t('response', 'Response'), ocppConsoleLogs.response)
                    }
                    title="Expand">
                    <ExpandIcon />
                  </button>
                </div>
              </div>
              <div className="ocpp-json-card-body">
                {(() => {
                  if (ocppConsoleLogs.isLoadingResponse && !ocppConsoleLogs.response) {
                    return (
                      <div className="ocpp-json-loading-state">
                        <span>{t('waitingForResponse', 'Waiting for response...')}</span>
                      </div>
                    );
                  }
                  if (ocppConsoleLogs.response) {
                    return <JsonCodeViewer data={ocppConsoleLogs.response} />;
                  }
                  return (
                    <div className="ocpp-json-empty-state">
                      <span>{t('noResponseReceived', 'No response received yet')}</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <div className="ocpp-operations-note-banner">
            {renderInfoIcon('ocpp-note-icon')}
            <span className="ocpp-note-text">
              <strong>{t('note')}:</strong> {t('ocppConsoleResponseNote')}
            </span>
          </div>
        </div>
      </div>
      <ModalBox
        open={expandedModal.isOpen}
        title={expandedModal.title}
        closeModal={() => setExpandedModal({ isOpen: false, title: '', data: null })}
        header
        rightCorner=""
        content={
          <div className="ocpp-expanded-json-wrap">
            <div className="ocpp-json-card-header-actions">
              <button
                type="button"
                className="ocpp-json-action-btn"
                onClick={() =>
                  handleCopy(JSON.stringify(expandedModal.data, null, 2), expandedModal.title)
                }
                title="Copy">
                <CopyIcon />
              </button>
            </div>
            <JsonCodeViewer data={expandedModal.data} />
          </div>
        }
      />
      <ModalBox
        open={showCancelFirmwareModal}
        closeModal={() => setShowCancelFirmwareModal(false)}
        title={null}
        header={false}
        rightCorner=""
        content={
          <DeleteWarning
            title={t('cancelFirmwareJobTitle', 'Cancel firmware update?')}
            description={t(
              'cancelFirmwareJobDescription',
              'Cancelling only stops tracking on our side — the charger may still complete or fail the update independently.',
            )}
            cancelButtonLabel={t('close', 'Close')}
            cancelButtonOnClick={() => setShowCancelFirmwareModal(false)}
            submitButtonLabel={
              cancellingFirmwareJob ? t('cancelling', 'Cancelling...') : t('cancelJob', 'Cancel Job')
            }
            submitButtonOnClick={handleCancelFirmwareJob}
          />
        }
      />
      <ModalBox
        open={showCancelDiagnosticsModal}
        closeModal={() => setShowCancelDiagnosticsModal(false)}
        title={null}
        header={false}
        rightCorner=""
        content={
          <DeleteWarning
            title={t('cancelDiagnosticsRequestTitle', 'Cancel diagnostics request?')}
            description={t(
              'cancelDiagnosticsRequestDescription',
              'Cancelling only stops tracking on our side — the charger may still upload a file for this request independently.',
            )}
            cancelButtonLabel={t('close', 'Close')}
            cancelButtonOnClick={() => setShowCancelDiagnosticsModal(false)}
            submitButtonLabel={
              cancellingDiagnosticsRequest
                ? t('cancelling', 'Cancelling...')
                : t('cancelRequest', 'Cancel Request')
            }
            submitButtonOnClick={handleCancelDiagnosticsRequest}
          />
        }
      />
    </div>
  );
}

Operations.defaultProps = {
  /**
   * Callback when action is triggered
   */
  onActionTriggered: () => {},
};
